<template>
  <div>
    <results-details-layout
      v-if="quote && Object.keys(quote).length"
      :class="$vuetify.breakpoint.mdAndDown ? 'pb-16' : undefined"
      @close-drawer="handleCloseDrawer"
    >
      <template #overview>
        <div
          class="results-details-overview"
        >
          <!--  Provider logo  -->
          <div class="d-flex flex-column justify-center text-center mb-3">
            <p>
              Your selected provider
            </p>
            <v-img
              :src="qd.imageSiteRemote"
              :alt="`${qd.imageSiteRemote} logo`"
              :height="35"
              :max-width="295"
              contain
            />
          </div>

          <v-divider class="mb-5" />

          <!--  Price  -->
          <div
            class="mb-5 mx-auto price-container"
          >
            <results-price
              :quote="quote"
              :monthly="false"
              :selected-optional-extras="selectedOptionalExtras"
              size="large"
              @open-drawer-details="$emit('open-drawer-details', ['details-description', 'estimatedPrice'])"
            />
          </div>
          <results-price-cashback
            v-if="qd.cashback"
            :cashback="+qd.cashback"
            :cashback-unit="qd.cashbackUnit"
            style-variant="info"
          />
          <v-divider class="mb-5" />

          <h2 class="mb-3 text-center">
            How to buy:
          </h2>

          <slot name="how-to-buy" />

          <!--  Buy online link  -->
          <template v-if="qd.buyOnlineShow">
            <v-btn
              :href="buyOnlineLink"
              x-large
              rounded
              block
              :disabled="isBuyOnlineDisabled"
              target="_blank"
              class="buyonline-link"
              @click="onClickToBuy(quote)"
            >
              <v-icon class="mr-3">
                mdi-open-in-new
              </v-icon>
              <span>Go to website</span>
            </v-btn>
          </template>

          <div
            class="mt-3 text-center"
          >
            <span class="d-block">{{ policyType }}</span>
            <span class="d-block">
              Cover level:
              <strong>{{ coverType }}</strong>
            </span>
          </div>

          <!--  Buyers note  -->
          <help-text class="mt-5 mb-5">
            <slot name="info">
              <h3 class="mb-2 fs-14">
                Please note:
              </h3>
              <p class="fs-14">
                If you've added any optional extras, you may need to add these on the
                insurance provider's site before you purchase
              </p>
            </slot>
          </help-text>

          <div v-if="qd.buyByPhoneShow && qd.telephone">
            <h2
              v-show="qd.buyOnlineShow"
              class="mb-3 text-center"
            >
              Or
            </h2>
            <v-btn
              v-show="!viewTelephone"
              x-large
              rounded
              block
              :color="qd.buyOnlineShow ? 'primary' : ''"
              :outlined="qd.buyOnlineShow"
              :class="{ 'buyonline-link': !qd.buyOnlineShow }"
              class="mb-4"
              @click="onClickToCall"
            >
              <v-icon
                class="mr-3"
              >
                mdi-phone
              </v-icon>
              <span>Call provider</span>
            </v-btn>
            <v-expand-transition>
              <div v-show="viewTelephone">
                <p class="mb-2 fs-13 font-weight-medium">
                  Phone number
                </p>
                <v-card
                  outlined
                  class="mb-4"
                >
                  <v-card-title class="body-1 font-weight-bold text-center justify-center fs-16">
                    {{ qd.telephone }}
                  </v-card-title>
                </v-card>
                <template v-if="qd.quoteReference">
                  <p class="mb-2 fs-13 font-weight-medium">
                    Quote reference number
                  </p>
                  <v-card outlined>
                    <v-card-title class="body-1 font-weight-bold text-center justify-center fs-16">
                      {{ qd.quoteReference }}
                    </v-card-title>
                  </v-card>
                </template>
                <p class="caption mt-4">
                  When buying this policy over the phone make sure you mention "Quotezone"
                  and your reference number to allow them to find your quote
                </p>
              </div>
            </v-expand-transition>
          </div>

          <v-divider />

          <template v-if="!qd.telephone && qd.quoteReference">
            <p class="mt-2 mb-2 fs-13 font-weight-medium">
              Quote reference number
            </p>
            <v-card outlined>
              <v-card-title class="body-1 font-weight-bold text-center justify-center fs-16">
                {{ qd.quoteReference }}
              </v-card-title>
            </v-card>
          </template>
        </div>
      </template>

      <template #breakdown>
        <!--  Policy features  -->
        <div class="results-details-breakdown">
          <h2 class="mb-3">
            Key features of this policy
          </h2>

          <p class="caption">
            Here's some information on the cover offered by the insurance provider, {{ qd.brokerFullName }}.
            Check the insurance provider's terms and conditions to find out more about what's included.
          </p>
          <help-text class="mt-5 mb-4">
            <slot name="info">
              <h3 class="mb-2 fs-14">
                Good to know
              </h3>
              <v-row
                no-gutters
                align="center"
                class="mb-3"
              >
                <v-col cols="1">
                  <v-icon color="primary">
                    mdi-check
                  </v-icon>
                </v-col>
                <v-col class="pl-3">
                  <p class="caption mb-0">
                    All items that are covered in this policy as standard are listed with a blue tick.
                  </p>
                </v-col>
              </v-row>
              <v-row
                no-gutters
                align="center"
                class="mb-3"
              >
                <v-col cols="1">
                  <v-icon color="accent">
                    mdi-check
                  </v-icon>
                </v-col>
                <v-col class="pl-3">
                  <p class="caption mb-0">
                    Additional policy extras you have chosen to include are listed with a purple tick.
                  </p>
                </v-col>
              </v-row>
              <v-row
                no-gutters
                align="center"
                class="mb-3"
              >
                <v-col cols="1">
                  <v-icon color="primary">
                    mdi-plus
                  </v-icon>
                </v-col>
                <v-col class="pl-3">
                  <p class="caption mb-0">
                    Items <strong>not</strong> covered in this policy,
                    but are available as an add on will be listed with a blue plus.
                  </p>
                </v-col>
              </v-row>
              <v-row
                no-gutters
                align="center"
              >
                <v-col cols="1">
                  <v-icon color="navy-dark">
                    mdi-close
                  </v-icon>
                </v-col>
                <v-col class="pl-3">
                  <p class="caption mb-0">
                    Items not available will be listed<br>
                    with an x.
                  </p>
                </v-col>
              </v-row>
            </slot>
          </help-text>
          <!--  COVERED  -->
          <template v-if="covered">
            <h4 class="mb-3 mt-5">
              What's covered in the policy
            </h4>
            <v-expansion-panels
              v-model="panelsCovered"
              multiple
              accordion
              flat
              class="mb-3"
            >
              <v-expansion-panel
                v-for="({ value, name }) in covered"
                :key="`covered-${name}`"
              >
                <div v-if="value.details">
                  <v-expansion-panel-header class="wrapped-header">
                    <span
                      class="body-2 text-capitalize mr-1"
                      v-html="getExtrasTitle(value, name)"
                    />
                    <v-icon
                      color="primary"
                      class="float-right align-cover-text"
                    >
                      mdi-check
                    </v-icon>
                    <ol class="optional-extra-list">
                      <li
                        v-for="(data, option) in filterDetails(value.details)"
                        :key="option"
                        class="optional-extra-list-item"
                      >
                        <span
                          class="body-2 text-capitalize mr-1"
                        >
                          {{ option.split(/(?=[A-Z])/).join(' ') }}
                        </span>
                        <div
                          v-if="data.cover !== null"
                          class="optional-extra"
                        >
                          <span
                            class="optional-extra__cover"
                          >
                            &pound;{{ helpers.convertExtraCover(data.cover) }}
                          </span>
                          <div
                            v-if="data.excess !== null"
                            class="optional-extra__excess optional-extra__excess--right"
                          >
                            <span>
                              Excess:&nbsp;
                            </span>
                            <span>
                              &pound;{{ helpers.convertExtraCover(data.excess) }}
                            </span>
                          </div>
                        </div>
                      </li>
                    </ol>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content v-scroll-to-viewport>
                    <p
                      v-for="(detail, index) in getExtrasDescription(name, value)"
                      :key="`detail-${index}`"
                      class="caption"
                    >
                      <span v-html="detail" />
                    </p>
                  </v-expansion-panel-content>
                </div>
                <div v-else>
                  <v-expansion-panel-header>
                    <span
                      class="body-2 text-capitalize mr-1 extra-title"
                      v-html="getExtrasTitle(value, name)"
                    />
                    <div class="extra-info">
                      <v-icon
                        color="primary"
                        class="float-right align-cover-text"
                      >
                        mdi-check
                      </v-icon>
                      <div
                        v-if="getOptionalExtraCover(name) !== null"
                        class="optional-extra"
                      >
                        <span
                          class="optional-extra__cover"
                        >
                          &pound;{{ getOptionalExtraCover(name).toLocaleString() }}
                        </span>
                        <div
                          v-if="getOptionalExtraExcess(name) !== null"
                          class="optional-extra__excess"
                        >
                          <span>
                            Excess:&nbsp;
                          </span>
                          <span>
                            &pound;{{ getOptionalExtraExcess(name).toLocaleString() }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content v-scroll-to-viewport>
                    <p
                      v-for="(detail, index) in getExtrasDescription(name, value)"
                      :key="`detail-${index}`"
                      class="caption"
                    >
                      <span v-html="detail" />
                    </p>
                  </v-expansion-panel-content>
                </div>
                <v-divider />
              </v-expansion-panel>
            </v-expansion-panels>
          </template>

          <!--  EXTRA  -->
          <template v-if="extra">
            <h4 class="mb-3">
              What's available as optional extras
            </h4>
            <v-expansion-panels
              v-model="panelsExtra"
              multiple
              accordion
              flat
              class="mb-3"
            >
              <v-expansion-panel
                v-for="({ value, name }) in extra"
                :key="`extra-${name}`"
              >
                <v-expansion-panel-header>
                  <span
                    class="body-2 text-capitalize mr-1"
                    v-html="getExtrasTitle(value, name)"
                  />
                  <div
                    v-if="getOptionalExtraCover(name) !== null"
                    class="optional-extra"
                  >
                    <span class="optional-extra__cover">
                      &pound;{{ getOptionalExtraCover(name).toLocaleString() }}
                    </span>
                    <div
                      v-if="getOptionalExtraExcess(name) !== null"
                      class="optional-extra__excess"
                    >
                      <span>
                        Excess:
                      </span>
                      <span>
                        &pound;{{ getOptionalExtraExcess(name).toLocaleString() }}
                      </span>
                    </div>
                  </div>
                  <template v-if="!Array.isArray(value) && isExtraChosenToInclude(name) && value.cost !== null">
                    <span
                      v-if="value.cost !== null && value.cost.value"
                      class="fs-12 grey--text align-cover-text"
                    >Added {{ value.cost.text }} £{{ value.cost.value.toFixed(2) }}</span>
                    <v-icon
                      color="accent"
                      class="float-right align-cover-text"
                    >
                      mdi-check
                    </v-icon>
                  </template>
                  <template v-else-if="!Array.isArray(value) && value.cost !== null && value.cost.value > 0">
                    <span
                      class="fs-12 grey--text align-cover-text"
                    >Available {{ value.cost.text }} £{{ value.cost.value.toFixed(2) }}</span>
                    <v-icon
                      color="primary"
                      class="float-right align-cover-text"
                    >
                      mdi-plus
                    </v-icon>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content v-scroll-to-viewport>
                  <p
                    v-for="(detail, index) in getExtrasDescription(name, value)"
                    :key="`detail-${index}`"
                    class="caption"
                  >
                    <span v-html="detail" />
                  </p>
                </v-expansion-panel-content>
                <v-divider />
              </v-expansion-panel>
            </v-expansion-panels>
          </template>
          <!--  NOT COVERED  -->
          <template v-if="notCovered">
            <h4 class="mb-3">
              What's <span style="text-decoration: underline;">not</span> covered in the policy
            </h4>
            <v-expansion-panels
              v-model="panelsNot"
              multiple
              accordion
              flat
              class="mb-3"
            >
              <v-expansion-panel
                v-for="({ value, name }) in notCovered"
                :key="`notCovered-${name}`"
              >
                <v-expansion-panel-header>
                  <span
                    class="body-2 text-capitalize mr-1"
                    v-html="getExtrasTitle(value, name)"
                  />
                  <v-icon
                    color="navy-dark"
                    class="float-right align-cover-text"
                  >
                    mdi-close
                  </v-icon>
                </v-expansion-panel-header>
                <v-expansion-panel-content v-scroll-to-viewport>
                  <p
                    v-for="(detail, index) in getExtrasDescription(name,value)"
                    :key="`detail-${index}`"
                    class="caption"
                  >
                    <span v-html="detail" />
                  </p>
                </v-expansion-panel-content>
                <v-divider />
              </v-expansion-panel>
            </v-expansion-panels>
          </template>

          <p class="fs-10">
            More cover options such as optional extras or upgrades
            may be available when you continue to the insurers website.
          </p>

          <v-divider class="mb-3" />

          <slot name="policy-excess" />
        </div>

        <change-quote-info
          :product-name="productName"
          @go-to-form="$emit('go-to-form')"
        />
      </template>
    </results-details-layout>
  </div>
</template>

<script>
import ResultsDetailsLayout from '@/components/car/Results/ResultsDetailsLayout.vue';
import ResultsPrice from '@/components/car/Results/ResultsPrice.vue';
import ResultsPriceCashback from '@/components/car/Results/ResultsPriceCashback.vue';
import ChangeQuoteInfo from '@/components/car/Results/ChangeQuoteInfo.vue';

import useQuotezoneCommon from '@/composables/useQuotezoneCommon';
import { inject } from '@vue/composition-api';

export default {
  name: 'results-details',
  inject: [
    'constants',
    'helpers',
  ],
  components: {
    ChangeQuoteInfo,
    ResultsDetailsLayout,
    ResultsPrice,
    ResultsPriceCashback,
  },
  props: {
    selectedPremium: {
      type: Object,
      default: undefined,
    },
    quote: {
      required: false,
      type: Object,
      default: undefined,
    },
    quotePositionNumber: {
      required: true,
      type: Number,
    },
    detailsDescription: {
      type: Object,
      required: true,
    },
    selectedOptionalExtras: {
      type: Array,
      required: true,
    },
    isDetailsDrawerOpen: {
      type: Boolean,
      required: true,
    },
    isBuyOnlineDisabled: {
      type: Boolean,
      default: false,
    },
    filterExtras: {
      type: Function,
      default: () => {},
    },
    policyType: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    panelsInsurer: [],
    panelsCovered: [],
    panelsExtra: [],
    panelsNot: [],
    viewTelephone: false,
  }),
  computed: {
    covered () {
      return this.filterExtras('Y');
    },
    qd () {
      return this.quote?.details?.[0] || null;
    },
    extra () {
      return this.filterExtras('extra');
    },
    notCovered () {
      return this.filterExtras('N');
    },
    buyOnlineLink () {
      return this.qd.proceedBuyOnlineLink;
    },
    // check if insured name only contains html entities (&nbsp; for example)
    hasInsurerName () {
      return !!this.qd.insurer.replace('&nbsp;', '');
    },
    insurerName () {
      return this.qd.insurer.replace('&nbsp;', ' ');
    },
    coverType () {
      return this.qd.coverType;
    },
  },
  watch: {
    isDetailsDrawerOpen () {
      this.hideTelephone();
    },
  },
  methods: {
    handleCloseDrawer () {
      if (this.selectedPremium) {
        this.$emit('open-drawer-details', [this.constants.DRAWER_COMPONENTS.coverBoostOptions, 'optionalExtras']);
        return;
      }

      this.$emit('close-drawer');
    },
    hideTelephone () {
      this.viewTelephone = false;
    },
    async onClickToBuy () {
      this.gtmTrackEvent('siteIdEvent', { siteId: this.quote.site_id });

      await this.$api.triggerEvent({
        eventName: 'clickToBuy',
      });

      const clicksAmount = this.clicksToBuyOnlineAmount;

      const buyOnlineClicked = () => this.internalTrackerResultsPage('buyOnlineClicked', true);
      const buyOnline = () => this.internalTrackerResultsPage(`buyOnlineClickCompany${clicksAmount}`, this.qd.brokerFullName);
      const buyOnlinePosition = () => this.internalTrackerResultsPage(`buyOnlineClickCompanyPosition${clicksAmount}`, this.quotePositionNumber);

      if (clicksAmount === 1) {
        buyOnlineClicked();
        buyOnline();
        buyOnlinePosition();

        this.increaseClicksBuyOnline();
        return;
      }

      if (clicksAmount <= this.constants.MAX_AMOUNT_OF_CLICKS) {
        buyOnline();
        buyOnlinePosition();

        this.increaseClicksBuyOnline();
      }
    },
    async onClickToCall () {
      this.viewTelephone = true;
      await this.$api.internalTracker(this.quote.site_id);

      const clicksAmount = this.clicksCallToProviderAmount;

      const phoneProviderClicked = () => this.internalTrackerResultsPage('phoneProviderClicked', true);
      const callProvider = () => this.internalTrackerResultsPage(`phoneProviderClickCompany${clicksAmount}`, this.qd.brokerFullName);
      const callProviderPosition = () => this.internalTrackerResultsPage(`phoneProviderClickCompanyPosition${clicksAmount}`, this.quotePositionNumber);

      if (clicksAmount === 1) {
        phoneProviderClicked();
        callProvider();
        callProviderPosition();

        this.increaseClicksCallToProvider();
        return;
      }

      if (clicksAmount <= this.constants.MAX_AMOUNT_OF_CLICKS) {
        callProvider();
        callProviderPosition();

        this.increaseClicksCallToProvider();
      }
    },
    getExtrasTitle (value, keySlug) {
      if (value.description && value.description !== 'Check with insurer') {
        // check if custom description passed in api
        return value.description;
      }
      const optionalExtraTitle = this.detailsDescription.content.find((el) => el.slug === keySlug);
      if (optionalExtraTitle && 'name' in optionalExtraTitle) {
        // check if title for the extra in the details.json file
        return optionalExtraTitle.name;
      }
      // fall back - replace underscores with spaces in keySlug and return
      return keySlug.replace(/_/g, ' ');
    },
    getExtrasDescription (key, value) {
      if (Array.isArray(value) && value.length) {
        return value;
      }

      const optionalExtraDescription = this.detailsDescription.content.find((el) => el.slug === key);

      if (optionalExtraDescription && 'description' in optionalExtraDescription) {
        const { slug, description } = optionalExtraDescription;
        const listOfExceptions = ['cruise', 'gadget', 'winterSports'];
        const exceptionIsIncluded = this.covered?.find(el => el.name === slug);

        return listOfExceptions.includes(slug) && exceptionIsIncluded
          ? [description[0]]
          : description;
      }

      return ['Please check your policy details with the insurance provider for full details of this cover.'];
    },
    isExtraChosenToInclude (key) {
      return this.selectedOptionalExtras.includes(key);
    },
    getOptionalExtraCover (name) {
      const coverAmount = this.qd?.optionalExtra[name]?.cover;

      return coverAmount === null ? null : this.helpers.convertExtraCover(coverAmount);
    },
    getOptionalExtraExcess (name) {
      const included = this.qd?.optionalExtra[name]?.included;

      return included ? (this.qd?.optionalExtra[name]?.excess ?? 0) : null;
    },
    filterDetails (details = {}) {
      return Object.fromEntries(
        // convert to array then map and fromEntries back to object
        Object.entries(details).filter(([key, value]) => {
          return value.included === 'Y';
        }),
      );
    },
  },
  setup (props, context) {
    const internalTrackers = inject('internalTrackers');

    return {
      ...useQuotezoneCommon(null, context),
      ...internalTrackers,
    };
  },
};
</script>

<style lang="scss" scoped>
.buy-online-checkbox ::v-deep .v-label {
  font-size: 12px;
}

.results-details-breakdown ::v-deep .v-expansion-panels .v-expansion-panel {
  background-color: transparent;
}

.results-details-breakdown ::v-deep .v-expansion-panel-content__wrap {
  background-color: #fff;
  padding: 6px 12px;
}

.align-cover-text {
  flex-grow: 0;
  margin-right: 10px;
}

.table + .table {
  margin-top: 10px;
}

.defaqto {
  width: 40%;
}

.v-expansion-panel-header {
  .extra-title {
    flex-basis: 40%;
  }

  .extra-info {
    flex-basis: 30%;
  }

  &.wrapped-header {
    flex-wrap: wrap;
  }
}

.optional-extra {
  position: relative;

  &-list {
    flex-basis: 100%;
    flex-shrink: 0;
    margin-top: 20px;
    padding-left: 0;
    order: 4;
    list-style-type: none;

    &-item {
      display: flex;
      margin-bottom: 20px;

      &::before {
        margin-right: 10px;
        content: "-";
      }

      .optional-extra {
        margin-left: auto;
      }
    }
  }

  &__cover {
    color: var(--v-primary-base, $clr-primary);
  }

  &__excess {
    position: absolute;
    bottom: -12px;
    left: 0;
    display: flex;
    font-size: 10px;

    &--right {
      right: 0;
      left: auto;
    }
  }
}

.price-container {
  max-width: 210px;
}
</style>
